import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from '@mlm/ui';
import { Placeholder } from './placeholder';
import { Stars } from './stars';
import { peso } from '../lib/format';
import { displaySellerName, useBrand, useProductHref, useStorefrontContent } from '../lib/hooks';
import { useCartStore } from '../lib/cart-store';
import type { DisplayProduct } from '../lib/types';

type Ribbon = { text: string; variant?: 'default' | 'new' | 'member' };

// `default` follows the tenant brand-dark; `member` uses the member gradient.
// `new` stays a fixed semantic green (it marks new arrivals, not brand).
const ribbonBg: Record<NonNullable<Ribbon['variant']>, string> = {
  default: 'hsl(var(--brand-dark))',
  new: '#0a8a4f',
  member: 'var(--member-gradient)',
};

/**
 * The marketplace product card used across every grid. `mall` shows the corner
 * OMNI MALL tag; `voucher` shows the bottom ₱-off band; `showPv` renders the green
 * "★ N PV · Member" badge (the MLM tie-in, over the real `pv` field).
 *
 * Virtual products get a Udemy-style hover panel (name, description, quick
 * Add to cart) beside the card. Hover-only by design — Radix HoverCard ignores
 * touch, so on mobile the card stays a plain tap-through link.
 */
export function ProductCard({
  p,
  i = 0,
  ribbon,
  mall,
  voucher,
  showPv,
}: {
  p: DisplayProduct;
  i?: number;
  ribbon?: Ribbon;
  mall?: boolean;
  voucher?: string;
  showPv?: boolean;
}) {
  // The star rating is a decorative hash-based figure (no real review data on the
  // card); the admin can hide it via the storefront `showDecorativeSold` toggle.
  const showDecorativeSold = useStorefrontContent().data.showDecorativeSold;
  const brand = useBrand();
  // Normally `/product/:id`; a virtual product with a campaign page assigned
  // opens that page when the tenant enables it. See `useProductHref`.
  const hrefFor = useProductHref();
  const card = (
    <Link
      to={hrefFor(p)}
      className="group block overflow-hidden rounded-[3px] border border-transparent bg-white transition-all duration-150 hover:-translate-y-0.5 hover:border-[#ffd9cb] hover:shadow-[0_8px_22px_rgba(0,0,0,.13)]"
    >
      <div className="relative aspect-square overflow-hidden">
        <Placeholder i={i} src={p.image} alt={p.name} />
        {mall && brand.showMallBadge && (
          <span className="absolute left-0 top-0 rounded-br-[6px] bg-o-d px-1.5 py-0.5 text-[9.5px] font-extrabold tracking-wide text-white">
            {brand.mallBadgeText}
          </span>
        )}
        {ribbon && (
          <span
            className="absolute left-0 top-0 rounded-br-[6px] px-1.5 py-0.5 text-[9.5px] font-extrabold tracking-wide text-white"
            style={{ background: ribbonBg[ribbon.variant ?? 'default'] }}
          >
            {ribbon.text}
          </span>
        )}
        {(voucher ?? p.voucherBadge) && (
          <span className="absolute inset-x-0 bottom-0 bg-o/[0.92] px-1.5 py-[3px] text-[10px] font-bold text-white">
            {voucher ?? p.voucherBadge}
          </span>
        )}
      </div>
      <div className="px-[9px] pb-[11px] pt-2">
        <div className="line-clamp-2 h-[33px] text-[12.5px] leading-[1.32] text-[#2c2c2c]">
          {p.name}
        </div>
        <div className="mt-[7px] text-[17px] font-extrabold text-o">
          <small className="text-[11px] font-bold">₱</small>
          {p.price.toLocaleString()}
        </div>
        {p.off > 0 && p.was > 0 && (
          <div className="mt-[3px] flex items-center gap-1.5 text-[11px]">
            <span className="text-[#aaa] line-through">{peso(p.was)}</span>
            <span className="font-bold text-o">-{p.off}%</span>
          </div>
        )}
        {showPv && p.pv > 0 && (
          <span className="mt-1.5 inline-block rounded-[3px] border border-pv-border bg-pv-bg px-1.5 py-0.5 text-[10px] font-bold text-pv">
            ★ {p.pv} PV · Member
          </span>
        )}
        {p.freeShipping && (
          <span className="ml-1.5 mt-1.5 inline-block rounded-[3px] border border-o/40 bg-o-l px-1.5 py-0.5 text-[10px] font-bold text-o">
            Free Shipping
          </span>
        )}
        <div className="mt-2 flex items-center justify-between text-[10.5px] text-[#999]">
          {showDecorativeSold ? <Stars rating={p.rating} /> : <span />}
          {p.stockQty != null &&
            (p.stockQty > 0 ? (
              <span>{p.stockQty.toLocaleString()} in stock</span>
            ) : (
              <span className="font-semibold text-o">Sold out</span>
            ))}
        </div>
      </div>
    </Link>
  );

  if (!p.isVirtual) return card;

  return (
    <HoverCard openDelay={250} closeDelay={100}>
      <HoverCardTrigger asChild>{card}</HoverCardTrigger>
      <HoverCardPortal>
        {/* `omni-store` re-applies the storefront token scope — the portal
            renders outside the page wrapper that normally carries it. */}
        <HoverCardContent
          side="right"
          align="start"
          collisionPadding={12}
          className="omni-store w-72"
        >
          <VirtualHoverPanel p={p} detailHref={hrefFor(p)} />
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}

/**
 * True when the card's href routes into a campaign landing page rather than the
 * PDP (`virtual_products_open_replicated_page` + an assigned page). The tenant
 * deliberately funnels ALL clicks on that product through the campaign page, so
 * the hover panel must not offer a direct Add-to-cart around it — and the
 * landing page has no variation picker, so "View options" would dead-end.
 */
function isCampaignFunnelHref(href: string): boolean {
  return href.startsWith('/r/') || href.startsWith('/c/');
}

/** The Udemy-style quick-look panel shown when hovering a virtual product. */
export function VirtualHoverPanel({ p, detailHref }: { p: DisplayProduct; detailHref: string }) {
  const brand = useBrand();
  const add = useCartStore((s) => s.add);
  const [added, setAdded] = useState(false);
  const hasVariants = p.variants.length > 0;
  const outOfStock = !hasVariants && p.stockQty != null && p.stockQty <= 0;

  // Straight port of the PDP add (product.tsx addToCart), single-SKU branch only
  // — variant products route to the detail page to pick a variation instead.
  function quickAdd() {
    add({
      productId: p.id,
      name: p.name,
      price: p.price,
      was: p.was || p.price,
      priceGc: p.priceGc,
      priceCoin: p.priceCoin,
      image: p.image,
      variation: '',
      variantId: undefined,
      pv: p.pv,
      cashback: p.cashback ?? 0,
      isVirtual: p.isVirtual,
      sellerId: p.sellerId,
      sellerName: displaySellerName(p.sellerId, p.sellerName, brand),
      qty: 1,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  }

  return (
    <div className="space-y-2.5">
      <div className="text-[14px] font-bold leading-snug text-[#2c2c2c]">{p.name}</div>
      <div className="flex items-center gap-2">
        <span className="text-[16px] font-extrabold text-o">
          <small className="text-[11px] font-bold">₱</small>
          {p.price.toLocaleString()}
        </span>
        {p.off > 0 && p.was > 0 && (
          <span className="text-[11px] text-[#aaa] line-through">{peso(p.was)}</span>
        )}
        {p.pv > 0 && (
          <span className="rounded-[3px] border border-pv-border bg-pv-bg px-1.5 py-0.5 text-[10px] font-bold text-pv">
            ★ {p.pv} PV
          </span>
        )}
      </div>
      {p.description && (
        <p className="line-clamp-5 whitespace-pre-line text-[12px] leading-[1.45] text-[#555]">
          {p.description}
        </p>
      )}
      {isCampaignFunnelHref(detailHref) ? (
        <Link
          to={detailHref}
          className="block w-full rounded-[3px] bg-o py-2 text-center text-[13px] font-bold text-white transition-opacity hover:opacity-90"
        >
          View offer
        </Link>
      ) : hasVariants ? (
        <Link
          to={detailHref}
          className="block w-full rounded-[3px] bg-o py-2 text-center text-[13px] font-bold text-white transition-opacity hover:opacity-90"
        >
          View options
        </Link>
      ) : outOfStock ? (
        <button
          type="button"
          disabled
          className="w-full cursor-not-allowed rounded-[3px] bg-[#e8e8e8] py-2 text-[13px] font-bold text-[#999]"
        >
          Sold out
        </button>
      ) : (
        <button
          type="button"
          onClick={quickAdd}
          className="flex w-full items-center justify-center gap-1.5 rounded-[3px] bg-o py-2 text-[13px] font-bold text-white transition-opacity hover:opacity-90"
        >
          {added ? (
            <>
              <Check className="h-4 w-4" /> Added
            </>
          ) : (
            'Add to cart'
          )}
        </button>
      )}
    </div>
  );
}
